import { useState, useEffect } from 'react';
import { NavLink, Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { Button, ActionIcon } from '@mantine/core';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import './DashboardLayout.css';

export default function DashboardLayout({ children }) {
  const { user, loading, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };
    window.addEventListener('resize', handleResize);
    // Initial check
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on mobile when navigating
  useEffect(() => {
    if (window.innerWidth <= 768) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  if (loading) {
    return <div className="dashboard-loading">{t('loading', 'Loading...')}</div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Normal user links (baseline)
  const renderNormalLinks = () => (
    <div className="nav-group">
      <h2 className="nav-group-title">{t('commerce', 'Commerce')}</h2>
      <NavLink to="/dashboard/orders" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
        {t('orders', 'Orders')}
      </NavLink>
      <NavLink to="/dashboard/settings" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
        {t('settings', 'Settings')}
      </NavLink>
      </div>
    );

  const handleOverlayKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsSidebarOpen(false);
    }
  };

  return (
    <div className="dashboard-layout">
      {/* Overlay background for mobile when sidebar is open */}
      <div
        className={`dashboard-sidebar-overlay ${isSidebarOpen ? 'show' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
        onKeyDown={handleOverlayKeyDown}
        role="button"
        tabIndex={isSidebarOpen ? 0 : -1}
        aria-label={t('closeSidebar', 'Close sidebar')}
      ></div>

      <aside
        id="dashboard-sidebar"
        className={`dashboard-sidebar ${isSidebarOpen ? 'open' : 'closed'}`}
        aria-label={t('dashboardSidebarLabel', 'Dashboard navigation')}
      >
        <div className="sidebar-header">
          <Link to="/" className="sidebar-home-link">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="home-icon" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
            <span>Yogi Foods</span>
          </Link>
        </div>

        <nav className="sidebar-nav" aria-label={t('dashboardNavLabel', 'Dashboard sections')}>
          {renderNormalLinks()}

          {user.is_admin ? (
            <div className="nav-group admin-group">
              <h2 className="nav-group-title">Administration</h2>
              <NavLink to="/dashboard/inventory" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Inventory
              </NavLink>
              <NavLink to="/dashboard/products" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Products
              </NavLink>
              <NavLink to="/dashboard/pricing" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Pricing
              </NavLink>
              <NavLink to="/dashboard/coupons" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Coupons &amp; Offers
              </NavLink>
              <NavLink to="/dashboard/customers" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                User Management
              </NavLink>
              <NavLink to="/dashboard/business-requests" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                {t('businessUpgradeRequests', 'Upgrade Requests')}
              </NavLink>
              <NavLink to="/dashboard/admin-orders" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Order Management
              </NavLink>
              <NavLink to="/dashboard/pickup-locations" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Pickup Locations
              </NavLink>
              <NavLink to="/dashboard/pickup-bookings" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                {t('pickupBookings', 'Pickup Bookings')}
              </NavLink>
              <NavLink to="/dashboard/admin-settings" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                General Settings
              </NavLink>
              <NavLink to="/dashboard/landing-page" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Landing Page / Banner
              </NavLink>
              <NavLink to="/dashboard/media" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Media Center
              </NavLink>
              <NavLink to="/dashboard/accounting" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                Bookkeeping
              </NavLink>
              <NavLink to="/dashboard/email-templates" className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                {t('emailTemplates', 'Email Templates')}
              </NavLink>
            </div>
          ) : null}
        </nav>
      </aside>
      
      <main className={`dashboard-content-area ${isSidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        <header className="dashboard-topbar">
           <ActionIcon
             variant="subtle"
             className="sidebar-toggle-btn"
             onClick={toggleSidebar}
             aria-label={isSidebarOpen ? t('closeSidebar', 'Close sidebar') : t('openSidebar', 'Open sidebar')}
             aria-expanded={isSidebarOpen}
             aria-controls="dashboard-sidebar"
           >
             <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
           </ActionIcon>
           <div className="topbar-user">
             <span className="topbar-user-name">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                {user.first_name} {user.last_name}
                {user.is_admin ? (
                  <span className="status-badge admin">
                    {t('adminRole', 'Admin')}
                  </span>
                ) : null}
             </span>
             <Button variant="subtle" color="gray" onClick={logout} className="topbar-logout">{t('logout', 'Logout')}</Button>
           </div>
        </header>

        <div className="dashboard-content">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}
